import Component from '../vendor/Component'
import KlevuRecsApiLayer from './KlevuRecsApiLayer'

export default class KlevuRecsTabs extends Component {
  constructor (elem, theme, options, ctx) {
    super(elem, theme, options, ctx)

    this.apiLayer = new KlevuRecsApiLayer()
    this.currentVariantId = this._elem.dataset.variantId

    this.tabEls = this._elem.querySelectorAll('[data-klevu-recs-tabs-el="tab"]')
    this.panelEls = this._elem.querySelectorAll('[data-klevu-recs-tabs-el="tabPanel"]')
  }

  mount () {
    this.setupListeners()
    this.cacheRecentlyViewed()
    this.renderFirstPanelContent()
    this.trackElementViews()
  }

  setupListeners () {
    this.tabEls.forEach(tabEl => {
      tabEl.addEventListener('click', this.handleTabClick.bind(this))
    });
  }

  renderFirstPanelContent (index) {
    const panelIndex = index || 0
    const firstPanel = this.panelEls[panelIndex]
    const recsType = firstPanel.getAttribute('data-recs-type')
    firstPanel.hidden = false

    const firstTab = document.querySelector(`[data-klevu-recs-tabs-el="tab"][data-recs-type="${recsType}"]`)
    firstTab.setAttribute('aria-selected', 'true')

    this.renderPanelContent(recsType)
  }

  async fetchProductData (recsType) {
    let apiResponse

    if (recsType === 'customers_also_viewed') {
      apiResponse = await this.apiLayer.fetchCustomersAlsoViewed(this.currentVariantId)
    } else if (recsType === 'recently_viewed') {
      apiResponse = await this.apiLayer.fetchRecentlyViewed()
    }

    return apiResponse.queryResults[0]?.records
  }

  async renderPanelContent (recsType) {
    const productData = await this.fetchProductData(recsType)

    if (productData.length > 0) {
      this.createCarouselItems(productData).then((items) => {
        this.renderCarousel(items, recsType)
      })
    } else {
      const emptyTab = document.querySelector(`[data-klevu-recs-tabs-el="tab"][data-recs-type="${recsType}"]`)
      const emptyPanel = document.querySelector(`[data-klevu-recs-tabs-el="tabPanel"][data-recs-type="${recsType}"]`)

      emptyTab.setAttribute('aria-selected', 'false')
      emptyTab.setAttribute('aria-hidden', 'true')
      emptyPanel.setAttribute('data-initialised', 'false')
      emptyPanel.hidden = true

      const emptyTabIndex = Array.from(this.panelEls).findIndex(el => el.dataset.recsType === recsType)
      this.renderFirstPanelContent(emptyTabIndex + 1);
    }
  }

  async createCarouselItems (productData) {
    const fragment = document.createDocumentFragment();

    const listItemPromises = productData.map(async (product) => {
      const productCardElement = await this.fetchProductCard(product);
      const productPriceHTML = await this.fetchProductPrice(product);

      const productCardUrl = productCardElement.querySelector('[data-klevu-markets-replace="url"]');
      productCardUrl.setAttribute('href', product.url);

      const productCardPrice = productCardElement.querySelector('[data-klevu-markets-replace="price"]');
      productCardPrice.innerHTML = productPriceHTML;

      const listItem = document.createElement('li');
      listItem.classList.add('swiper-slide');
      listItem.setAttribute('data-gtm-el', 'klevu-recs-product-card');
      listItem.setAttribute('data-gtm-item-id', product.id);
      listItem.setAttribute('data-gtm-item-title', product.name);
      listItem.appendChild(productCardElement);

      return listItem;
    });

    const listItems = await Promise.all(listItemPromises);

    listItems.forEach(listItem => {
      fragment.appendChild(listItem);
    });

    return fragment
  }

  renderCarousel (fragment, recsType) {
    const panelElsArray = Array.from(this.panelEls)
    const carousel = panelElsArray.filter(el => {
      return el.dataset.recsType === recsType
    })[0]

    const carouselWrapper = carousel.querySelector('[data-klevu-recs-tabs-el="carouselWrapper"]')
    carouselWrapper.appendChild(fragment)

    this.trackProductCardClicks(carouselWrapper)
    this.initialiseCarousel(carousel)
    this._elem.setAttribute('data-initialised', 'true')

    window.dispatchEvent(new CustomEvent('recsTabs:rendered'));
  }

  initialiseCarousel (carousel) {
    const swiper = new window.swiper(`[data-recs-type="${carousel.dataset.recsType}"] .swiper`, {
      direction: 'horizontal',
      slidesPerView: 2.5,
      spaceBetween: 6,
      centeredSlidesBounds: true,
      breakpoints: {
        480: {
          slidesPerView: 2,
        },
        640: {
          slidesPerView: 3,
        },
        768: {
          slidesPerView: 4,
        },
        1024: {
          slidesPerView: 5,
        },
      },
    })
  }

  fetchProductCard (product) {
    const productHandle = product.url.split('/').pop()

    return fetch(`${window.Shopify.routes.root}products/${productHandle}?section_id=search-overlay-product-card`)
      .then(response => {
        if (!response.ok) throw new Error('Failed to fetch product card');
        return response.text();
      })
      .then(text => {
        const html = new DOMParser().parseFromString(text, 'text/html');

        return html.querySelector('.prd-Card');
      });
  }

  fetchProductPrice (product) {
    const productHandle = product.url.split('/').pop()

    return fetch(`${window.Shopify.routes.root}products/${productHandle}?section_id=product-price`)
      .then(response => {
        if (!response.ok) throw new Error('Failed to fetch product price');
        return response.text();
      })
      .then(text => {
        const result = new DOMParser().parseFromString(text, 'text/html');
        const resultHTML = result.getElementById('shopify-section-product-price').innerHTML;

        return resultHTML;
      });
  }

  handleTabClick (event) {
    const tab = event.currentTarget
    const clickedRecsType = tab.getAttribute('data-recs-type')

    this.toggleTabs(clickedRecsType)
    this.togglePanels(clickedRecsType)

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'klevu_recs_tab_clicked',
      target_text: clickedRecsType
    });
  }

  toggleTabs (clickedRecsType) {
    const tabElsArray = Array.from(this.tabEls)

    tabElsArray.forEach(el => {
      const isClickedTab = el.dataset.recsType === clickedRecsType
      el.setAttribute('aria-selected', isClickedTab)
    })
  }

  async togglePanels(clickedRecsType) {
    const panelElsArray = Array.from(this.panelEls)

    for (const el of panelElsArray) {
      const isClickedTab = el.dataset.recsType === clickedRecsType
      const carouselIsInitialised = el.getAttribute('data-initialised') === 'true'

      el.setAttribute('hidden', '')

      if (isClickedTab) {
        if (!carouselIsInitialised) {
          this.renderPanelContent(clickedRecsType)
        }

        el.removeAttribute('hidden')
      }
    }
  }

  cacheRecentlyViewed () {
    let recentlyViewedVariantIds = JSON.parse(localStorage.getItem('BAO-recentlyViewedProducts')) || [];

    recentlyViewedVariantIds = recentlyViewedVariantIds.filter(id => id !== this.currentVariantId);
    recentlyViewedVariantIds.unshift(this.currentVariantId);

    if (recentlyViewedVariantIds.length > 5) {
      recentlyViewedVariantIds = recentlyViewedVariantIds.slice(0, 5);
    }

    localStorage.setItem('BAO-recentlyViewedProducts', JSON.stringify(recentlyViewedVariantIds));
  }

  trackElementViews() {
    const targetElement = document.querySelector('[data-klevu-recs-tabs]');
    const headerHeight = '127px';

    const observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'klevu_recs_tabs_viewed',
            });

            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: `-${headerHeight} 0px 0px 0px`,
        threshold: 0
      }
    );

    observer.observe(targetElement);
  }

  trackProductCardClicks(carouselWrapper) {
    const carouselItems = carouselWrapper.querySelectorAll('[data-gtm-el=klevu-recs-product-card]');

    if (carouselItems.length <= 0) return

    carouselItems.forEach(el => {
      el.addEventListener('click', (event) => {
        const itemId = el.getAttribute('data-gtm-item-id');
        const itemTitle = el.getAttribute('data-gtm-item-title');
        const targetContext = this._elem.querySelector('[data-klevu-recs-tabs-el="tab"][aria-selected="true"]').textContent.trim();

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'klevu_recs_product_card_clicked',
          item_id: itemId,
          item_title: itemTitle,
          target_context: targetContext
        });
      });
    });
  }
}
